import React, { useRef, useState } from "react";
import { Box, Button, FormControl, InputLabel, MenuItem, Select, Typography, Chip } from "@mui/material";
import LanguageToggle from "../../../components/LanguageToggle/LanguageToggle";
import { useTranslation } from "react-i18next";

const ProductionCenterHeader = ({
    isLoading,
    productionCenters,
    selectedCenterName,
    onChangeCenter,
    onRefresh,
    statusOptions,
    selectedStatuses,
    onChangeStatuses,
    totalPendingItems,
    selectionCount = 0,
    focusActive = false,
    onExpandSelected,
    onExitFocus,
    selectedOrderObjects = [],
    onReorderSelectedOrders,
}) => {
    const { t } = useTranslation();
    const dragIndex = useRef(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    const handleDragStart = (index) => (e) => {
        dragIndex.current = index;
        e.dataTransfer.effectAllowed = "move";
    };
    const handleDragOver = (index) => (e) => {
        e.preventDefault();
        setDragOverIndex(index);
    };
    const handleDrop = (index) => (e) => {
        e.preventDefault();
        const from = dragIndex.current;
        const to = index;
        setDragOverIndex(null);
        if (from === null || to === null || from === to) return;
        const newOrder = [...selectedOrderObjects];
        const [moved] = newOrder.splice(from, 1);
        newOrder.splice(to, 0, moved);
        onReorderSelectedOrders?.(newOrder.map((o) => o.name));
    };

    return (
        <>
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 2,
            }}
        >
            <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel id="pc-select-label">
                    {t("production.productionCenters")}
                </InputLabel>
                <Select
                    labelId="pc-select-label"
                    multiple
                    value={Array.isArray(selectedCenterName) ? selectedCenterName : (selectedCenterName ? [selectedCenterName] : [])}
                    label={t("production.productionCenters")}
                    renderValue={(selected) =>
                        (selected || [])
                            .map((val) => {
                                const pc = (productionCenters || []).find((p) => p.name === val);
                                return pc?.description || pc?.name || val;
                            })
                            .join(", ")
                    }
                    onChange={(e) => {
                        const next = e.target.value;
                        // Support both array-state and string-state parents
                        if (Array.isArray(selectedCenterName)) {
                            onChangeCenter(next);
                        } else {
                            onChangeCenter(next?.[0] || "");
                        }
                    }}
                >
                    {(productionCenters || []).map((pc) => (
                        <MenuItem key={pc.name} value={pc.name}>
                            {pc.description || pc.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Button variant="outlined" onClick={onRefresh} disabled={isLoading}>
                {t("common.refresh")}
            </Button>

            <LanguageToggle />

            <FormControl
                size="small"
                sx={{ minWidth: 240 }}
                disabled={statusOptions.length === 0}
            >
                <InputLabel id="status-multi-select-label">
                    {t("production.statuses")}
                </InputLabel>
                <Select
                    labelId="status-multi-select-label"
                    label={t("production.statuses")}
                    multiple
                    value={selectedStatuses}
                    renderValue={(selected) => selected.join(", ")}
                    onChange={(e) => {
                        // e.target.value is the full ordered array in MUI Select multiple
                        onChangeStatuses(e.target.value);
                    }}
                >
                    {statusOptions.map((s) => (
                        <MenuItem key={s} value={s}>
                            {s}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Typography variant="body2" sx={{ color: "#6E7187" }}>
                {t("production.pendingItems", { count: totalPendingItems })}
            </Typography>

            {focusActive ? (
                <Button variant="contained" color="secondary" onClick={onExitFocus}>
                    {t("production.exitFocus")}
                </Button>
            ) : selectionCount >= 2 ? (
                <Button variant="contained" onClick={onExpandSelected}>
                    {t("production.expandSelected", { count: selectionCount })}
                </Button>
            ) : null}
        </Box>
        {selectionCount >= 2 && !focusActive && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
                <Typography variant="caption" sx={{ color: "#6E7187" }}>
                    {t("production.reorder")}
                </Typography>
                {selectedOrderObjects.map((o, idx) => (
                    <Chip
                        key={o.name}
                        label={o.table_description || o.name}
                        draggable
                        onDragStart={handleDragStart(idx)}
                        onDragOver={handleDragOver(idx)}
                        onDrop={handleDrop(idx)}
                        sx={{
                            border: dragOverIndex === idx ? "1px dashed #e14954" : undefined,
                            cursor: "grab",
                        }}
                    />
                ))}
            </Box>
        )}
        </>
    );
};

export default ProductionCenterHeader;


